import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  InputLabel,
  Card,
  IconButton,
  TextField,
  Popover,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import CustomInput from "../../../../../components/CustomInput/CustomInput";
import {
  getComparisons,
  createComparison,
  updateComparison,
  deleteComparison,
} from "../../../viewServices/calls/apiCalls";
import {
  grayColor,
  whiteColor,
} from "../../../../../assets/jss/material-dashboard-pro-react";

const styles = (clientColor) => ({
  card: {
    minWidth: 400,
    maxWidth: 500,
    borderRadius: "4px",
    border: "none",
    overflow: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    padding: "20px 24px",
  },
  cardTitle: {
    color: "#888",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "16px",
    textAlign: "center",
  },
  comparisonItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 0",
  },
  comparisonItemContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  editIcon: {
    fontSize: "16px",
    padding: "4px",
    color: grayColor[6],
    "&:hover": {
      color: clientColor,
      backgroundColor: grayColor[13],
    },
  },
  deleteIcon: {
    fontSize: "16px",
    padding: "4px",
    color: grayColor[6],
    "&:hover": {
      color: "#f44336",
      backgroundColor: "#ffebee",
    },
  },
  confirmIcon: {
    fontSize: "16px",
    padding: "4px",
    color: grayColor[6],
    "&:hover": {
      color: "#4caf50",
      backgroundColor: "#e8f5e9",
    },
  },
  cancelIcon: {
    fontSize: "16px",
    padding: "4px",
    color: grayColor[6],
    "&:hover": {
      color: "#f44336",
      backgroundColor: "#ffebee",
    },
  },
  comparisonNameInput: {
    "& .MuiInputBase-root": {
      fontSize: "14px",
    },
    "& input": {
      padding: "4px 8px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: `${grayColor[4]}`,
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: `${grayColor[4]}`,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: clientColor,
    },
  },
  popover: {
    padding: "16px",
  },
  popoverButtons: {
    display: "flex",
    gap: "8px",
    marginTop: "12px",
  },
  selectFormControl: {
    width: "100%",
    "& .MuiInputBase-root": {
      fontSize: "14px",
    },
    "& > div": {
      "&:before": {
        borderBottomColor: `${grayColor[4]}!important`,
      },
      "&:after": {
        borderBottomColor: `${clientColor}!important`,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: `${clientColor}!important`,
      },
    },
  },
  createButton: {
    backgroundColor: clientColor,
    color: whiteColor,
    padding: "8px 20px", // Smaller than original (was 12px 24px)
    fontSize: "13px", // Smaller than original (was 14px)
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "6px", // Keep original radius
    width: "100%",
    "&:hover": {
      backgroundColor: clientColor,
      opacity: 0.9,
    },
    "&:disabled": {
      backgroundColor: grayColor[10],
      color: grayColor[6],
    },
  },
  toggleLink: {
    marginTop: "20px",
    textAlign: "left",
    fontSize: "12px",
    color: clientColor,
    cursor: "pointer",
    "&:hover": {
      color: clientColor,
      textDecoration: "underline",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px",
  },
  selectorLoadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px 0",
  },
  helpText: {
    fontSize: "12px",
    color: grayColor[5],
    marginTop: "4px",
  },
});

const ComparisonSetupCard = ({
  onComparisonSelect,
  scenarios: scenariosProp = [],
}) => {
  const { userDetails } = useContext(AuthContext);
  const client_styles = () => ({
    ...styles(userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();
  const clientColor = userDetails.implementation_color;

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [showCreateForm, setShowCreateForm] = useState(false); // Default to select existing
  const [comparisons, setComparisons] = useState([]);
  const [selectedComparisonId, setSelectedComparisonId] = useState("");
  const [isLoadingComparisons, setIsLoadingComparisons] = useState(false);
  // Use scenarios from prop (provided by ScenarioDataContext via parent components)
  const scenarios = scenariosProp;
  const [selectedScenario1, setSelectedScenario1] = useState("");
  const [selectedScenario2, setSelectedScenario2] = useState("");
  const [comparisonName, setComparisonName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Edit/Delete state
  const [editingComparisonId, setEditingComparisonId] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [comparisonToDelete, setComparisonToDelete] = useState(null);

  useEffect(() => {
    const fetchComparisons = async () => {
      setIsLoadingComparisons(true);
      try {
        const response = await getComparisons();
        if (response.status === "success") {
          setComparisons(response.data || []);
        }
      } catch (err) {
        console.error("Error fetching comparisons:", err);
        setComparisons([]);
      } finally {
        setIsLoadingComparisons(false);
      }
    };
    fetchComparisons();
  }, []);

  // Scenarios are now provided via props from ScenarioDataContext (no duplicate API call)

  const handleComparisonChange = (event) => {
    const comparisonId = event.target.value;
    setSelectedComparisonId(comparisonId);
    const comparison = comparisons.find(
      (c) => c.comparison_id === comparisonId,
    );
    if (comparison && onComparisonSelect) {
      // Trigger loading state immediately before selection
      if (window.priceroute_setLoadingState) {
        window.priceroute_setLoadingState();
      }
      onComparisonSelect(comparison);
    }
  };

  const handleCreateComparison = async () => {
    if (!selectedScenario1 || !selectedScenario2) {
      toast.warning(t("comparative.toast.select_both_scenarios"));
      return;
    }
    if (selectedScenario1 === selectedScenario2) {
      toast.warning(t("comparative.toast.select_different_scenarios"));
      return;
    }
    if (!comparisonName.trim()) {
      toast.warning(t("comparative.toast.enter_comparison_name"));
      return;
    }

    setIsCreating(true);
    try {
      const scenario1Data = scenarios.find(
        (s) => s.scenario_id === selectedScenario1,
      );
      const scenario2Data = scenarios.find(
        (s) => s.scenario_id === selectedScenario2,
      );
      const finalComparisonName = comparisonName.trim();

      const response = await createComparison({
        comparison_name: finalComparisonName,
        scenario_a_id: selectedScenario1,
        scenario_b_id: selectedScenario2,
      });

      if (response.status === "success") {
        toast.success(t("comparative.toast.comparison_created"));
        const newComparison = {
          comparison_id: response.comparison_id,
          comparison_name: finalComparisonName,
          scenario_a: {
            scenario_id: selectedScenario1,
            scenario_name:
              scenario1Data?.scenario_name || `Scenario ${selectedScenario1}`,
          },
          scenario_b: {
            scenario_id: selectedScenario2,
            scenario_name:
              scenario2Data?.scenario_name || `Scenario ${selectedScenario2}`,
          },
        };

        const updatedComparisons = await getComparisons();
        setComparisons(updatedComparisons.data || []);
        setComparisonName("");
        setSelectedScenario1("");
        setSelectedScenario2("");
        setShowCreateForm(false);
        // Trigger loading state immediately before selection
        if (window.priceroute_setLoadingState) {
          window.priceroute_setLoadingState();
        }
        onComparisonSelect(newComparison);
      } else {
        toast.error(t("comparative.toast.comparison_create_failed"));
      }
    } catch (err) {
      console.error("Error creating comparison:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to create comparison";
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  // Handlers for editing comparison name
  const handleEditClick = (event, comparison) => {
    event.stopPropagation();
    setEditingComparisonId(comparison.comparison_id);
    setEditNameValue(comparison.comparison_name);
  };

  const handleSaveEdit = async (comparisonId) => {
    if (!editNameValue.trim()) {
      toast.warning(t("comparative.toast.comparison_name_empty"));
      return;
    }

    const newName = editNameValue.trim();
    const oldComparison = comparisons.find(
      (c) => c.comparison_id === comparisonId,
    );
    const oldName = oldComparison?.comparison_name;

    // Optimistic update - update UI immediately
    setComparisons((prevComparisons) =>
      prevComparisons.map((c) =>
        c.comparison_id === comparisonId
          ? { ...c, comparison_name: newName }
          : c,
      ),
    );

    // Close editing mode immediately
    setEditingComparisonId(null);
    setEditNameValue("");

    // Show success toast immediately
    toast.success(t("comparative.toast.comparison_name_updated"));

    // Sync with PriceRoute selector
    if (window.priceroute_refreshComparisons) {
      window.priceroute_refreshComparisons();
    }

    // Send request to backend in background
    try {
      await updateComparison(comparisonId, { comparison_name: newName });

      // Refresh to ensure sync with backend
      const response = await getComparisons();
      if (response.status === "success") {
        setComparisons(response.data || []);
        // Sync again after backend confirms
        if (window.priceroute_refreshComparisons) {
          window.priceroute_refreshComparisons();
        }
      }
    } catch (error) {
      console.error("Failed to update comparison name:", error);

      // Rollback optimistic update on error
      setComparisons((prevComparisons) =>
        prevComparisons.map((c) =>
          c.comparison_id === comparisonId
            ? { ...c, comparison_name: oldName }
            : c,
        ),
      );

      // Rollback PriceRoute selector too
      if (window.priceroute_refreshComparisons) {
        window.priceroute_refreshComparisons();
      }

      toast.error(t("comparative.toast.comparison_name_update_failed"));
    }
  };

  const handleCancelEdit = () => {
    setEditingComparisonId(null);
    setEditNameValue("");
  };

  // Handlers for deleting comparison
  const handleDeleteClick = (event, comparison) => {
    event.stopPropagation();
    setDeleteAnchorEl(event.currentTarget);
    setComparisonToDelete(comparison);
  };

  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setComparisonToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!comparisonToDelete) return;

    const idToDelete = comparisonToDelete.comparison_id;
    const nameToDelete = comparisonToDelete.comparison_name;
    const deletedComparison = comparisonToDelete;

    // Close popover immediately
    setDeleteAnchorEl(null);
    setComparisonToDelete(null);

    // Optimistic update - remove from UI immediately
    setComparisons((prevComparisons) =>
      prevComparisons.filter((c) => c.comparison_id !== idToDelete),
    );

    // Show success toast immediately
    toast.success(
      t("comparative.toast.comparison_deleted", { name: nameToDelete }),
    );

    // Sync with PriceRoute selector
    if (window.priceroute_refreshComparisons) {
      window.priceroute_refreshComparisons();
    }

    // Send delete request to backend in background
    try {
      await deleteComparison(idToDelete);

      // Refresh to ensure sync with backend
      const response = await getComparisons();
      if (response.status === "success") {
        setComparisons(response.data || []);
        // Sync again after backend confirms
        if (window.priceroute_refreshComparisons) {
          window.priceroute_refreshComparisons();
        }
      }
    } catch (error) {
      console.error("Failed to delete comparison:", error);

      // Rollback optimistic update on error
      setComparisons((prevComparisons) => {
        // Re-insert the deleted comparison
        const newComparisons = [...prevComparisons, deletedComparison];
        // Sort by created_at to maintain original order
        return newComparisons.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
      });

      // Rollback PriceRoute selector too
      if (window.priceroute_refreshComparisons) {
        window.priceroute_refreshComparisons();
      }

      toast.error(
        t("comparative.toast.comparison_delete_failed", { name: nameToDelete }),
      );
    }
  };

  const openDeletePopover = Boolean(deleteAnchorEl);

  return (
    <>
      {showCreateForm ? (
        <Card className={classes.card}>
          <Typography className={classes.cardTitle}>
            {t("comparative.setup.create_title")}
          </Typography>
          <Box
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Box>
              <CustomInput
                labelText={t("comparative.setup.comparison_name_label")}
                id="comparison-name"
                formControlProps={{
                  fullWidth: true,
                  required: selectedScenario1 && selectedScenario2,
                }}
                onChange={(e) => setComparisonName(e.target.value)}
                value={comparisonName}
                inputProps={{
                  placeholder:
                    selectedScenario1 && selectedScenario2
                      ? t(
                          "comparative.setup.comparison_name_placeholder_required",
                        )
                      : t(
                          "comparative.setup.comparison_name_placeholder_first",
                        ),
                }}
                client
                noPaddingTop
              />
            </Box>
            <Box
              style={{ display: "flex", gap: "16px", alignItems: "flex-end" }}
            >
              <Box style={{ flex: 1 }}>
                <FormControl
                  className={classes.selectFormControl}
                  fullWidth
                  size="small"
                >
                  <InputLabel id="scenario-a-label">
                    {t("comparative.setup.scenario_a_label")}
                  </InputLabel>
                  <Select
                    labelId="scenario-a-label"
                    value={selectedScenario1}
                    onChange={(e) => setSelectedScenario1(e.target.value)}
                    label={t("comparative.setup.scenario_a_label")}
                    disabled={scenarios.length === 0}
                  >
                    <MenuItem value="">
                      <em>{t("comparative.setup.select_placeholder")}</em>
                    </MenuItem>
                    {scenarios.map((scenario) => (
                      <MenuItem
                        key={scenario.scenario_id}
                        value={scenario.scenario_id}
                        disabled={scenario.scenario_id === selectedScenario2}
                      >
                        {scenario.scenario_name ||
                          `Scenario ${scenario.scenario_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box style={{ flex: 1 }}>
                <FormControl
                  className={classes.selectFormControl}
                  fullWidth
                  size="small"
                >
                  <InputLabel id="scenario-b-label">
                    {t("comparative.setup.scenario_b_label")}
                  </InputLabel>
                  <Select
                    labelId="scenario-b-label"
                    value={selectedScenario2}
                    onChange={(e) => setSelectedScenario2(e.target.value)}
                    label={t("comparative.setup.scenario_b_label")}
                    disabled={scenarios.length === 0}
                  >
                    <MenuItem value="">
                      <em>{t("comparative.setup.select_placeholder")}</em>
                    </MenuItem>
                    {scenarios.map((scenario) => (
                      <MenuItem
                        key={scenario.scenario_id}
                        value={scenario.scenario_id}
                        disabled={scenario.scenario_id === selectedScenario1}
                      >
                        {scenario.scenario_name ||
                          `Scenario ${scenario.scenario_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Button
              className={classes.createButton}
              onClick={handleCreateComparison}
              disabled={
                !selectedScenario1 ||
                !selectedScenario2 ||
                selectedScenario1 === selectedScenario2 ||
                !comparisonName.trim() ||
                isCreating
              }
              variant="contained"
            >
              {isCreating
                ? t("comparative.setup.creating")
                : t("comparative.setup.create_button")}
            </Button>
            {scenarios.length === 0 && (
              <Typography
                className={classes.helpText}
                style={{ textAlign: "center" }}
              >
                {t("comparative.setup.no_scenarios_help")}
              </Typography>
            )}
          </Box>
          <Typography
            className={classes.toggleLink}
            onClick={() => comparisons.length > 0 && setShowCreateForm(false)}
            style={{
              opacity: comparisons.length === 0 ? 0.4 : 1,
              cursor: comparisons.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            {t("comparative.setup.select_existing_link")}
          </Typography>
        </Card>
      ) : (
        <Card className={classes.card}>
          <Typography className={classes.cardTitle}>
            {t("comparative.setup.select_title")}
          </Typography>
          {isLoadingComparisons ? (
            <Box className={classes.selectorLoadingContainer}>
              <ThreeDots
                height="30"
                width="30"
                radius="9"
                color={clientColor}
                visible={true}
              />
            </Box>
          ) : (
            <FormControl
              className={classes.selectFormControl}
              size="small"
              fullWidth
            >
              <InputLabel id="comparison-selector-label">
                {comparisons.length === 0
                  ? t("comparative.setup.no_comparisons_label")
                  : t("comparative.setup.choose_comparison_label")}
              </InputLabel>
              <Select
                labelId="comparison-selector-label"
                value={selectedComparisonId}
                onChange={handleComparisonChange}
                label={
                  comparisons.length === 0
                    ? t("comparative.setup.no_comparisons_label")
                    : t("comparative.setup.choose_comparison_label")
                }
                disabled={comparisons.length === 0}
                renderValue={(selected) => {
                  const comparison = comparisons.find(
                    (c) => c.comparison_id === selected,
                  );
                  return comparison?.comparison_name || "";
                }}
              >
                {comparisons.map((comparison) => (
                  <MenuItem
                    key={comparison.comparison_id}
                    value={comparison.comparison_id}
                  >
                    <Box
                      className={classes.comparisonItem}
                      style={{ width: "100%" }}
                    >
                      <Box className={classes.comparisonItemContent}>
                        {editingComparisonId === comparison.comparison_id ? (
                          <>
                            <TextField
                              value={editNameValue}
                              onChange={(e) => setEditNameValue(e.target.value)}
                              autoFocus
                              fullWidth
                              size="small"
                              className={classes.comparisonNameInput}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <IconButton
                              size="small"
                              className={classes.confirmIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEdit(comparison.comparison_id);
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.cancelIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEdit();
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <span style={{ flex: 1 }}>
                            {comparison.comparison_name}
                          </span>
                        )}
                      </Box>
                      {editingComparisonId !== comparison.comparison_id &&
                        !editingComparisonId && (
                          <>
                            <IconButton
                              size="small"
                              className={classes.editIcon}
                              onClick={(e) => handleEditClick(e, comparison)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.deleteIcon}
                              onClick={(e) => handleDeleteClick(e, comparison)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography
            className={classes.toggleLink}
            onClick={() => {
              setShowCreateForm(true);
              setComparisonName("");
              setSelectedScenario1("");
              setSelectedScenario2("");
            }}
          >
            {t("comparative.setup.create_new_link")}
          </Typography>
        </Card>
      )}

      {/* Delete confirmation popover */}
      <Popover
        open={openDeletePopover}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box className={classes.popover}>
          <Typography>
            {t("comparative.setup.delete_confirm_title", {
              name: comparisonToDelete?.comparison_name,
            })}{" "}
            {t("comparative.setup.delete_confirm_description")}
          </Typography>
          <Box className={classes.popoverButtons}>
            <Button
              onClick={handleDeleteConfirm}
              color="secondary"
              variant="contained"
              size="small"
            >
              {t("comparative.setup.confirm")}
            </Button>
            <Button onClick={handleDeleteClose} variant="outlined" size="small">
              {t("comparative.setup.cancel")}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

ComparisonSetupCard.propTypes = {
  onComparisonSelect: PropTypes.func.isRequired,
  scenarios: PropTypes.array,
};

export default ComparisonSetupCard;
